import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Calendar,
  Clock,
  Copy,
  Eye,
  FolderInput,
  Shapes,
  FolderKanban,
  Globe,
  Heart,
  Hourglass,
  Layers,
  MessageSquare,
  MoreHorizontal,
  RotateCcw,
  Share2,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  ApprovalState,
  ApprovalType,
  ContentChannel,
  canReleasePost,
  isPostFailed,
  scheduleLockReason,
} from '@mg/shared';
import { useTenant } from '../../auth/TenantContext';
import { TestModeFailureNote } from '../testMode/TestModeBanner';
import { isTestModeFailure } from '../testMode/testMode';
import type { Post } from '@mg/shared';
import {
  displayStateLabel,
  failureChipState,
  displayStateTone,
  type PostDisplayState as PostDisplayStateT,
  channelLabel,
  formatDateTime,
  formatMetric,
  metricsPending,
  formatRelative,
} from '../../lib/display';
import { cn } from '../../lib/cn';
import { remPx } from '../../lib/rem';
import { Badge, type BadgeTone } from '../../components/ui/Badge';
import { postTypeIcon } from './postTypes';
import { PlatformIcon } from './PlatformIcon';
import { PostThumbnail } from './PostThumbnail';
import { PostCreatorAvatar } from './PostCreatorAvatar';
import { CategoryPill } from '../categories/CategoryPill';
import { threadCountLabel, threadUnconfirmedText } from '../composer/thread';
import { ThreadUnconfirmedNotice } from '../composer/ThreadUnconfirmedNotice';
import {
  ON_APPROVAL_LABEL,
  RESCHEDULE_LABEL,
  UNSCHEDULED_LABEL,
  postScheduleState,
} from './scheduleState';

/** Distinct badge tone per post type so cards read at a glance. */
const channelTone: Record<ContentChannel, BadgeTone> = {
  [ContentChannel.BLOG]: 'blue',
  [ContentChannel.SOCIAL]: 'brand',
  [ContentChannel.VIDEO]: 'amber',
  [ContentChannel.PODCAST]: 'neutral',
};

export interface PostCardActions {
  onEdit: () => void;
  onSchedule: () => void;
  onDelete: () => void;
  onPublish: () => void;
  /** Copy the post (any status) into a campaign — opens the campaign picker. */
  onDuplicate: () => void;
  /** Re-parent an editable (draft) post — opens the campaign picker. */
  onMoveToCampaign: () => void;
  /** Download this post's copy, images and brand as editable Canva/Figma artboards. */
  onGenerateDesign: () => void;
  /**
   * Open the campaign this post belongs to. Optional: the campaign page itself leaves it out,
   * because the only place it could go is where you already are.
   */
  onGoToCampaign?: () => void;
}

/**
 * The status a post DISPLAYS — the single source of truth for both the badge and the ⋯ menu's
 * state gating, so an action can never be offered for a status the card doesn't show. Mirrors
 * `postBadgeState` (PostsView) but reads the campaign's state from the prop rather than the
 * denormalized field: POST mode uses the post's OWN approval **when it has one** (a post created
 * under CAMPAIGN mode has `approvalState: null` and keeps inheriting the campaign even after the
 * workspace flips to Per-Post — treating that null as DRAFT would badge it "Published" while
 * offering draft-only actions); NONE mode has no approval to advance, so it derives from the
 * release timestamps; CAMPAIGN mode inherits the campaign's aggregate.
 */
export function postDisplayState(
  post: Post,
  campaignState: ApprovalState,
  approvalType: ApprovalType,
): ApprovalState {
  /**
   * A post that has gone out is Published, whatever mode approved it.
   *
   * Checked FIRST, before the mode branches. In CAMPAIGN mode a post carries no state of its own —
   * the campaign's label is shown instead — so a post that published while its campaign was still
   * "Approved" read as Approved, with a publish date sitting next to it. The publish date is the
   * fact; the campaign's label is a rollup that may not have caught up (and, if its release step
   * ever failed, never will).
   */
  if (post.publishedAt) return ApprovalState.PUBLISHED;
  if (approvalType === ApprovalType.POST && post.approvalState) return post.approvalState;
  if (approvalType === ApprovalType.NONE) {
    return post.scheduledAt ? ApprovalState.SCHEDULED : campaignState;
  }
  return campaignState;
}

/**
 * Unified post card. A Post targets ONE destination (channel + platform). Release
 * (schedule/publish/delete) and the STATUS BADGE are BOTH mode-aware, mirroring the
 * server gates (posts.service.ts): in CAMPAIGN mode the owning campaign's aggregate
 * governs; in POST mode the post's own `approvalState` governs; in NONE mode there is
 * no approval gate and release/badge follow the post's own publish/schedule state.
 *
 * `showCampaign` adds a chip linking back to the owning campaign (used on the flat
 * cross-campaign posts view; omitted on the campaign detail page).
 */
export function PostCard({
  post,
  campaignState,
  approvalType = ApprovalType.CAMPAIGN,
  actions,
  showCampaign = false,
}: {
  post: Post;
  campaignState: ApprovalState;
  /** Workspace approval policy — decides whether the badge shows the post's own state. */
  approvalType?: ApprovalType;
  actions: PostCardActions;
  showCampaign?: boolean;
}) {
  const { activeTenant } = useTenant();
  const channel = post.channel;
  const TypeIcon = postTypeIcon[channel];
  const isSocial = channel === ContentChannel.SOCIAL;
  const contentCount = post.contents?.length;

  const badgeState = postDisplayState(post, campaignState, approvalType);
  // The CHIP may read Failed; the gating above deliberately keeps the real approval state, since
  // "failed" says nothing about what actions are allowed (a failed post is still rescheduleable).
  const chipState: PostDisplayStateT = failureChipState(post) ?? badgeState;
  const awaitingNames = post.pendingApproverNames ?? [];
  const awaiting =
    (badgeState === ApprovalState.IN_REVIEW || badgeState === ApprovalState.COMPLIANCE_CHECK) &&
    awaitingNames.length > 0;

  // A publish attempt that hit the native platform and failed: the server records the
  // real reason on the post and leaves publishedAt null, so the post stays releasable and
  // the still-available "Publish" action retries it. Surface the reason at a glance.
  const publishFailed = !!post.publishError && post.publishedAt == null;
  /**
   * The other way a post fails: the workspace is in test mode, so the release worker skipped it
   * entirely and its time simply passed. There is no publishError to show — which is exactly why
   * the card needs to say something, or it reads as "Failed" with no reason anywhere on screen.
   */
  const testModeFailed = isTestModeFailure(post, badgeState, activeTenant);

  // Shared branching, local wording — see postScheduleState. This card used to stop after
  // `scheduledAt`, so "Post as soon as approved" was reported as "Not scheduled".
  const schedule = postScheduleState(post);
  const scheduleLabel =
    schedule.kind === 'published'
      ? `Published ${formatRelative(schedule.at)}`
      : schedule.kind === 'scheduled'
        ? `Scheduled for ${formatDateTime(schedule.at)}`
        : schedule.kind === 'on-approval'
          ? ON_APPROVAL_LABEL
          : schedule.kind === 'reschedule-required'
            ? RESCHEDULE_LABEL
            : UNSCHEDULED_LABEL;

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-card shadow-card">
      <div className="flex">
        {/* The thumbnail runs down the card's whole left edge at a fixed width, cropped to cover;
            a post with no image gets the placeholder at the same size. It adds no height of its own
            (the text decides how tall the card is), and the card's overflow-hidden rounds its outer
            corners, so it has none of its own. 8.25rem (132px) from sm up; 5rem on a phone, where
            132px left the title a few characters and ran the type badge under "Edit →". */}
        <div className="relative w-20 shrink-0 self-stretch sm:w-[8.25rem]">
          <PostThumbnail
            fill
            mediaKind={post.mediaKind}
            thumbnailUrl={post.thumbnailUrl}
            iconClassName="h-8 w-8"
          />
        </div>

        {/* A COLUMN, so the metadata row below spans the full content width and the engagement
            lands under the View link rather than stopping short of it. The card's padding lives
            here now, so the thumbnail can meet the card's edges. */}
        <div className="flex min-w-0 flex-1 flex-col p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <Badge tone={channelTone[channel]}>
                <TypeIcon className="mr-1 h-3.5 w-3.5" aria-hidden />
                {channelLabel[channel]}
              </Badge>
              <Badge tone={displayStateTone(chipState)}>
                {displayStateLabel(chipState)}
              </Badge>
              {isSocial ? (
                <PlatformIcon platform={post.platform} />
              ) : (
                post.platformLabel && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted">
                    <Globe className="h-3.5 w-3.5" aria-hidden />
                    {post.platformLabel}
                  </span>
                )
              )}
            </div>

            {showCampaign && post.campaignName && (
              <Link
                to={`/campaigns/${post.campaignId}`}
                className="focus-ring mb-1 inline-flex max-w-full items-center gap-1 rounded text-xs font-medium text-link hover:text-action-hover hover:underline"
              >
                <FolderKanban className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">{post.campaignName}</span>
              </Link>
            )}

              <h3 className="truncate text-lg font-semibold text-ink">{post.title}</h3>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {/* Keyed on publishedAt, not on the badge: in CAMPAIGN mode a post that HAS gone out
                  still displays its campaign's state (Approved/Scheduled) until the last sibling
                  publishes, and in NONE mode the badge is derived. publishedAt is the one value that
                  means "this is live", and the composer is read-only for exactly that set. */}
              <button
                type="button"
                onClick={actions.onEdit}
                className="focus-ring rounded-md px-2 py-1 text-sm font-medium text-link hover:bg-tint"
              >
                {post.publishedAt ? 'View →' : 'Edit →'}
              </button>
              <PostMenu
                post={post}
                campaignState={campaignState}
                approvalType={approvalType}
                actions={actions}
              />
            </div>
          </div>

          {/* Full content width: schedule/pieces left, engagement hard right under the View link.
              From sm up the schedule group is ONE line (nowrap + truncate) instead of shattering into
              five and dragging every card taller; overflow-hidden is the hard stop that keeps anything
              from painting outside the card. Below sm (a phone, where the text column beside the
              thumbnail is narrower than that line) the one line was cut mid-word ("Scheduled for
              Oct 20, 12:"), so there the group wraps at word boundaries instead: the label wraps inside
              itself, and the pieces count moves to the next line whole. Who created the post and its
              scheduling category are a group of their own after it: when the card is too narrow for
              both, that group moves to a second line (a wrapping row moves the item that does not fit),
              so the schedule never wraps for it and it is never clipped.
              That group, and the pieces count, open with their own "·", which must not lead a line. So
              each wrapping row is pulled 1.25rem left inside a clipping box: whatever starts a line
              starts 1.25rem early, the row's first item's ml-5 gives that back, and a "·" (a w-5 slot)
              that starts a line lands outside the clip. The 5s go together, in both rows. */}
          <div className="mt-2 flex items-center justify-between gap-3 overflow-hidden text-sm text-ink-muted">
            <div className="min-w-0 overflow-hidden">
              <div className="-ml-5 flex flex-wrap items-center gap-y-1">
                <div className="ml-5 min-w-0 overflow-hidden sm:truncate">
                  <span className="-ml-5 flex flex-wrap items-center gap-y-1 sm:flex-nowrap">
                    {/* Its icon stays on the first line when the words wrap. */}
                    <span className="ml-5 flex min-w-0 items-start gap-1.5 sm:shrink-0">
                      <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                      {scheduleLabel}
                    </span>
                    {/* A thread says how many POSTS it is - its rows would read "24 pieces" - and, until
                        it is published, how many of them are already live. Read off `post.thread`, which
                        every list carries, so it shows where the post came without its rows too. */}
                    {post.thread && (
                      <AfterSchedule>
                        <Layers className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        {threadCountLabel(post.thread.total)}
                        {post.publishedAt == null &&
                          post.thread.published > 0 &&
                          ` · ${post.thread.published} of ${post.thread.total} published`}
                      </AfterSchedule>
                    )}
                    {!post.thread && contentCount != null && (
                      <AfterSchedule>
                        <Layers className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        {contentCount} piece{contentCount === 1 ? '' : 's'}
                      </AfterSchedule>
                    )}
                  </span>
                </div>
                <CreatorAndCategory post={post} />
              </div>
            </div>
            <PostEngagement post={post} />
          </div>

            {awaiting && (
              <p className="mt-1.5 flex max-w-full items-center gap-1.5 truncate text-xs text-amber-600 dark:text-warning-dark">
                <Hourglass className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">Awaiting: {awaitingNames.join(', ')}</span>
              </p>
            )}

            {testModeFailed && (
              <p className="mt-2 break-words text-xs text-red-600 dark:text-error-dark">
                <TestModeFailureNote />
              </p>
            )}

            {publishFailed && (
              <div className="mt-2">
                <Badge tone="red">
                  <AlertCircle className="mr-1 h-3.5 w-3.5" aria-hidden />
                  Publish failed
                </Badge>
                <p className="mt-1 break-words text-xs text-red-600 dark:text-error-dark">{post.publishError}</p>
              </div>
            )}

            {/* A thread stopped on a post nobody could confirm went out: nothing more of it is sent
                until someone says what happened to that post. Asked here, under the failure. */}
            {post.thread?.unconfirmed && post.publishedAt == null && (
              <div className="mt-2">
                <ThreadUnconfirmedNotice post={post} />
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

/**
 * What follows the schedule label in its group - the pieces count, or the thread's: its own "·", centred in a
 * w-5 slot like the creator group's (about the space either side the group's gap gave it before), then the icon
 * and the words. The group's row clips the slot away when this starts a line, which happens only below sm, where
 * the row wraps; there its words may wrap too. From sm up it keeps its width, on the group's one line.
 */
function AfterSchedule({ children }: { children: ReactNode }) {
  return (
    <span className="flex min-w-0 items-start sm:shrink-0">
      <span aria-hidden className="w-5 shrink-0 text-center">
        ·
      </span>
      <span className="flex min-w-0 items-start gap-1.5">{children}</span>
    </span>
  );
}

/**
 * Who created the post and the scheduling category it queues in, on every card whatever its status:
 * the creator's avatar and name ("Unknown" for a post made before creators were recorded), then the
 * category pill ("General" when it has none).
 *
 * The avatar is decorative because the name is printed beside it. The hidden words give a screen
 * reader what a sighted reader gets from the avatar and the pill's look. The group can shrink: on a
 * line of its own that is still too narrow, the name and the pill shorten with an ellipsis rather
 * than being cut off at the card's edge.
 *
 * The "·" sits centred in a w-5 slot (the same space either side as the row's other dots), which the
 * meta row clips away when this group starts a line: see the comment there.
 */
function CreatorAndCategory({ post }: { post: Post }) {
  const creator = post.createdBy ?? null;
  return (
    <div className="flex min-w-0 items-center">
      <span aria-hidden className="w-5 shrink-0 text-center">
        ·
      </span>
      <span className="flex min-w-0 items-center gap-1.5">
        <PostCreatorAvatar person={creator} size="xs22" decorative />
        <span className="max-w-[10rem] truncate" title={creator?.name ?? 'Creator unknown'}>
          <span className="sr-only">Created by </span>
          {creator?.name ?? 'Unknown'}
        </span>
      </span>
      <span className="sr-only">Scheduling category: </span>
      <CategoryPill category={post.category} className="ml-2" />
    </div>
  );
}

const METRIC_SEGMENTS = [
  { key: 'views', Icon: Eye, label: 'Views' },
  { key: 'likes', Icon: Heart, label: 'Likes' },
  { key: 'comments', Icon: MessageSquare, label: 'Comments' },
  { key: 'shares', Icon: Share2, label: 'Shares' },
] as const;

/** shrink-0 so the numbers never wrap; hidden on the narrowest cards, where they cannot fit. */
const SEGMENTS_CLS = 'shrink-0 items-center gap-2';
/**
 * The word beside each number is the first thing to go: icons + values alone are ~270px against
 * ~440px with labels, which is the difference between fitting and shredding the left-hand text on
 * a laptop-width window.
 */
const LABEL_CLS = 'hidden text-ink-faint lg:inline';

/**
 * Engagement on the metadata row of a PUBLISHED post: icon + value + label, right-aligned.
 *
 * Unlike the tile and the table, an unavailable metric is OMITTED here rather than dashed — this
 * row is metadata, not a grid, so a varying segment count is fine and a row of dashes would be
 * noise. Three states, same as everywhere else: no `metrics` object means the post has no platform
 * id and can never be measured (render nothing at all); a null `fetchedAt` means we simply haven't
 * asked yet (shimmer, sized to the real thing so the row doesn't jump); a set `fetchedAt` is a
 * settled answer, so each null field is a metric the platform withholds and is dropped.
 */
function PostEngagement({ post }: { post: Post }) {
  if (post.publishedAt == null || post.metrics == null) return null;

  if (metricsPending(post)) {
    return (
      <div className={cn(SEGMENTS_CLS, 'hidden sm:flex')}>
        {/* Mirrors the settled markup element for element — same icon, same label, a bar only
            where the number goes — so the row is the same width before and after the values land.
            A bare bar was ~40% narrower, and the difference was paid for by re-wrapping the left
            text and growing every card. */}
        {METRIC_SEGMENTS.map(({ key, Icon, label }) => (
          <span key={key} className="flex items-center gap-1.5">
            <Icon className="h-4 w-4" aria-hidden />
            <span className="h-4 w-8 animate-pulse rounded bg-chip" aria-hidden />
            <span className={LABEL_CLS}>{label}</span>
          </span>
        ))}
        {/* Outside aria-hidden, or a screen reader is told nothing about the row at all. */}
        <span className="sr-only">Loading engagement</span>
      </div>
    );
  }

  // Not pending and never fetched → nothing will arrive; omit the segment entirely rather than
  // leaving a shimmer running forever.
  if (post.metrics.fetchedAt == null) return null;

  const segments = METRIC_SEGMENTS.flatMap(({ key, Icon, label }) => {
    const value = post.metrics?.[key];
    // `== null` deliberately, not falsy: a genuine 0 is a real result and must render as 0.
    return value == null ? [] : [{ key, Icon, label, value }];
  });
  if (segments.length === 0) return null;

  return (
    <div className={cn(SEGMENTS_CLS, 'hidden sm:flex')}>
      {segments.map(({ key, Icon, label, value }, i) => (
        <span key={key} className="flex items-center gap-1.5">
          {i > 0 && (
            <span className="mr-0.5 text-ink-ghost" aria-hidden>
              ·
            </span>
          )}
          <Icon className="h-4 w-4" aria-hidden />
          <span className="font-semibold text-ink">{formatMetric(value)}</span>
          <span className={LABEL_CLS}>{label}</span>
        </span>
      ))}
    </div>
  );
}

export function PostMenu({
  post,
  campaignState,
  approvalType,
  actions,
}: {
  post: Post;
  campaignState: ApprovalState;
  approvalType: ApprovalType;
  actions: PostCardActions;
}) {
  const [open, setOpen] = useState(false);
  // Menu is portaled to <body> and fixed-positioned off the button, so it can't be clipped when
  // the trigger lives inside a table's overflow-x-auto scroller (a plain absolute menu would be).
  const [pos, setPos] = useState<{ right: number; top?: number; bottom?: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // A fixed menu would drift if the page/table scrolled while open — close it instead.
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  // Release gating mirrors posts.service.ts exactly, so no shown action 400s:
  //  - CAMPAIGN: the owning campaign's aggregate governs (APPROVED/SCHEDULED → schedule;
  //    an existing schedule date OR APPROVED/SCHEDULED → publish; DRAFT/REVISE → delete).
  //  - POST: the post's OWN approvalState governs the same way (a sibling's state is irrelevant).
  //  - NONE: no approval gate — a not-yet-published post can schedule/publish/delete.
  // Schedule/Publish disappear once the post is published (the server rejects re-release).
  const postState = post.approvalState ?? ApprovalState.DRAFT;
  const published = post.publishedAt != null;

  // A PROPOSED schedule may be set while editable (DRAFT/REVISE) — required before submit — as well
  // as after approval (release): the server's own rule (scheduleLockReason), on the post's own (lane)
  // state first, so a failed post reopened in a released campaign can be given a time again.
  const canSchedule =
    !published && scheduleLockReason(approvalType, post.approvalState ?? null, campaignState) === null;
  // THE SAME FUNCTION the server gates on — see canReleasePost. The hand-written copy that used
  // to live here had drifted into being stricter than the server (it read only the campaign's
  // state in CAMPAIGN mode), which is why a failed post in a published campaign had nothing to
  // click: the menu hid a publish the API would have accepted.
  const canPublish = canReleasePost(post, campaignState, approvalType);
  // Deletable in ANY state EXCEPT published — mirrors the server gate. `publishedAt` is the ground
  // truth for "this went live" (NONE mode never moves approvalState), so that's what we key on
  // rather than editability: a rejected or in-review post can be cleaned up, a live one can't.
  // Nor can a thread part of which is live, or may be: its live posts would be orphaned on the
  // platform, and the server refuses.
  const partLive = post.thread != null && (post.thread.published > 0 || post.thread.unconfirmed);
  const canDelete =
    post.publishedAt == null && postState !== ApprovalState.PUBLISHED && !partLive;
  // Duplicate is offered for EVERY post in EVERY state (standalone or in a campaign): the server
  // always creates a fresh DRAFT copy, so nothing about the source's approval/release state gates
  // it. It is also what keeps the ⋯ button rendered for a published post, which has no other action.
  const canDuplicate = true;
  // "Retry publish" rather than "Publish now" — the same action under the name the Failed tab
  // sends people looking for.
  const failed = isPostFailed(post);
  // A thread stopped on a post nobody could confirm went out: nothing more of it is sent until
  // someone says what happened to that post (the card asks, under the failure): the server refuses
  // a publish, and the composer holds scheduling. Both stay in the menu, disabled, with the card's
  // own sentence as the reason - an action that is always refused should not be offered.
  const unconfirmed =
    post.thread?.unconfirmed && post.publishedAt == null
      ? threadUnconfirmedText(post.platform, post.thread)
      : null;
  // Offered only where it goes somewhere else: the All Posts views pass the handler, the campaign
  // page does not (you are already on that campaign). A standalone post has no campaign to open.
  const canGoToCampaign = Boolean(actions.onGoToCampaign && post.campaignId);
  // Move re-parents an EDITABLE post. In POST/CAMPAIGN mode that means the badge must read Draft
  // — matched via postDisplayState, never `postState` (which coerces a null approvalState to DRAFT
  // and would offer Move on a post badged Published/Approved after a CAMPAIGN→POST mode switch).
  // NONE mode has no approval to advance: the server's editability check falls through to the
  // campaign, which NONE leaves at DRAFT, so every unpublished post is movable there — including a
  // scheduled one, whose badge reads "Scheduled" but which is still just an unreleased draft
  // (this mirrors Delete, likewise unconditional in NONE mode).
  const canMove =
    approvalType === ApprovalType.NONE
      ? !published
      : postDisplayState(post, campaignState, approvalType) === ApprovalState.DRAFT;
  const hasActions =
    canSchedule || canPublish || canGoToCampaign || canDuplicate || canMove || canDelete;

  // `whitespace-nowrap` keeps every label on ONE line — the 2.25rem-per-item estimate below (and the
  // flip-up calculation that depends on it) assumes no wrapping.
  const itemCls =
    'focus-ring flex w-full items-center gap-2 whitespace-nowrap px-3 py-2 text-left text-sm text-ink-strong hover:bg-hover disabled:cursor-not-allowed disabled:text-ink-faint';

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  // Duplicate keeps the menu non-empty today; the guard stays for the day any of these is gated.
  if (!hasActions) return null;

  // Position the fixed menu off the button, flipping above when there isn't room below.
  const openMenu = () => {
    const el = btnRef.current;
    if (!el) {
      setOpen(true);
      return;
    }
    const r = el.getBoundingClientRect();
    // Estimated height = py-1 (0.5rem) + 2.25rem per item + the divider above Delete when anything
    // precedes it (my-1 and its 1px line). Keep this in sync with the items rendered below or the
    // flip-up drifts. In rem like the menu itself, so the sum holds when the UI is scaled to 80%;
    // the gaps around the menu are too.
    const count =
      (canSchedule ? 1 : 0) +
      (canPublish ? 1 : 0) +
      (canGoToCampaign ? 1 : 0) +
      (canDuplicate ? 1 : 0) +
      (canMove ? 1 : 0) +
      // "Generate design…" is unconditional, and was missing from this tally — the estimate ran a
      // whole item short, which matters more now the menu can hold one more.
      1 +
      (canDelete ? 1 : 0);
    const above = true; // Generate design always sits above Delete.
    const rem = remPx();
    const estH = (0.5 + count * 2.25) * rem + (canDelete && above ? 0.5 * rem + 1 : 0);
    const spaceBelow = window.innerHeight - r.bottom;
    const flipUp = spaceBelow < estH + 0.75 * rem && r.top > spaceBelow;
    setPos({
      right: Math.max(0.5 * rem, Math.round(window.innerWidth - r.right)),
      ...(flipUp
        ? { bottom: Math.round(window.innerHeight - r.top + 0.25 * rem) }
        : { top: Math.round(r.bottom + 0.25 * rem) }),
    });
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className="focus-ring rounded-md p-1.5 text-ink-faint hover:bg-chip hover:text-ink-strong"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', right: pos.right, top: pos.top, bottom: pos.bottom }}
            className="z-50 w-48 overflow-hidden rounded-lg border border-line bg-card py-1 shadow-panel"
          >
            {canSchedule && (
              <button
                type="button"
                role="menuitem"
                className={itemCls}
                disabled={unconfirmed != null}
                title={unconfirmed ?? undefined}
                onClick={() => run(actions.onSchedule)}
              >
                <Calendar className="h-4 w-4 text-ink-faint" aria-hidden />
                Schedule
              </button>
            )}
            {canPublish && (
              <button
                type="button"
                role="menuitem"
                className={itemCls}
                disabled={unconfirmed != null}
                title={unconfirmed ?? undefined}
                onClick={() => run(actions.onPublish)}
              >
                {/* Same action either way — the label says which situation the user is in, so a
                    post sitting in the Failed tab offers the words they came looking for. */}
                {failed ? (
                  <>
                    <RotateCcw className="h-4 w-4 text-ink-faint" aria-hidden />
                    Retry publish
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 text-ink-faint" aria-hidden />
                    Publish now
                  </>
                )}
              </button>
            )}
            {canGoToCampaign && (
              <button
                type="button"
                role="menuitem"
                className={itemCls}
                onClick={() => run(actions.onGoToCampaign as () => void)}
              >
                <FolderKanban className="h-4 w-4 text-ink-faint" aria-hidden />
                Go to campaign
              </button>
            )}
            {canDuplicate && (
              <button
                type="button"
                role="menuitem"
                className={itemCls}
                onClick={() => run(actions.onDuplicate)}
              >
                <Copy className="h-4 w-4 text-ink-faint" aria-hidden />
                Duplicate
              </button>
            )}
            {canMove && (
              <button
                type="button"
                role="menuitem"
                className={itemCls}
                onClick={() => run(actions.onMoveToCampaign)}
              >
                <FolderInput className="h-4 w-4 text-ink-faint" aria-hidden />
                Move to campaign
              </button>
            )}
            {/* Offered for every status — a published post is exactly what you want to remix. */}
            <button
              type="button"
              role="menuitem"
              className={itemCls}
              onClick={() => run(actions.onGenerateDesign)}
            >
              <Shapes className="h-4 w-4 text-ink-faint" aria-hidden />
              Generate design…
            </button>
            {canDelete && (
              <>
                {(canSchedule || canPublish || canDuplicate || canMove) && (
                  <div className="my-1 border-t border-line-soft" />
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="focus-ring flex w-full items-center gap-2 whitespace-nowrap px-3 py-2 text-left text-sm text-red-600 dark:text-error-dark hover:bg-red-50 dark:hover:bg-error-dark-tint"
                  onClick={() => run(actions.onDelete)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Delete
                </button>
              </>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
